import storefront from "../global/storefront";
import { PRODUCT_GROUP_SWATCH_EXPANSION_QUERY } from "../global/storefront/queries";

const MAX_IDS_PER_NODES_REQUEST = 250;

// Products with no Color Family sort after every family (matches snippet.product-color-group.liquid).
const NO_COLOR_FAMILY_POSITION = 9999;

const groupDataCache = new Map();

function chunk(array, size) {
  const chunks = [];

  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }

  return chunks;
}

function extractSwatchAppearance(node) {
  const colorPatternNode =
    node && node.colorPattern && node.colorPattern.references && node.colorPattern.references.nodes[0];

  if (!colorPatternNode) {
    return null;
  }

  return {
    imageUrl:
      (colorPatternNode.swatchImage &&
        colorPatternNode.swatchImage.reference &&
        colorPatternNode.swatchImage.reference.image &&
        colorPatternNode.swatchImage.reference.image.url) ||
      null,

    color: (colorPatternNode.swatchColor && colorPatternNode.swatchColor.value) || null,

    label: (colorPatternNode.swatchLabel && colorPatternNode.swatchLabel.value) || null,
  };
}

// Lowest "position" across the product's custom.color_family_list metaobjects.
function extractColorFamilyPosition(node) {
  const families =
    (node && node.colorFamilyList && node.colorFamilyList.references && node.colorFamilyList.references.nodes) || [];

  return families.reduce((lowest, family) => {
    const position = Number.parseInt(family && family.position && family.position.value, 10);

    return Number.isFinite(position) && position < lowest ? position : lowest;
  }, NO_COLOR_FAMILY_POSITION);
}

function normalizeFabricLabel(value = "") {
  return value
    .replace(/ Fabric$/i, "")
    .trim()
    .toLowerCase();
}

// Product's own fabric: custom.fabric_new, then legacy custom.fabric (single or list reference).
function extractFabric(node) {
  const metafields = [node && node.fabricNew, node && node.fabric];

  for (const metafield of metafields) {
    const reference = metafield && (metafield.reference || (metafield.references && metafield.references.nodes[0]));

    if (reference && reference.handle) {
      return {
        handle: reference.handle,
        label: normalizeFabricLabel((reference.label && reference.label.value) || ""),
      };
    }
  }

  return null;
}

function extractFieldProducts(field) {
  return ((field && field.references && field.references.nodes) || []).map((node) => ({
    id: node.id.split("/").pop(),
    title: node.title,

    url: window.productUrl(node.handle),
    available: node.availableForSale,
    imageUrl: node.featuredImage && node.featuredImage.url,
    swatch: extractSwatchAppearance(node),
    colorFamilyPosition: extractColorFamilyPosition(node),
    fabric: extractFabric(node),
  }));
}

async function fetchGroupData(groupOptionIds) {
  const batches = chunk(groupOptionIds, MAX_IDS_PER_NODES_REQUEST);

  const responses = await Promise.all(
    batches.map((batchIds) =>
      storefront
        .request({
          query: PRODUCT_GROUP_SWATCH_EXPANSION_QUERY,
          variables: {
            ids: batchIds,
          },
        })
        .catch((error) => {
          console.error("[product-swatch-group-expansion] Storefront batch request failed:", error);

          return undefined;
        }),
    ),
  );

  batches.forEach((batchIds, batchIndex) => {
    const response = responses[batchIndex];
    const nodes = response && response.data && response.data.nodes;

    if (!Array.isArray(nodes)) {
      return;
    }

    nodes.forEach((node, nodeIndex) => {
      const groupOptionId = batchIds[nodeIndex];

      groupDataCache.set(
        groupOptionId,
        node
          ? {
              solids: extractFieldProducts(node.solids),
              florals: extractFieldProducts(node.florals),
            }
          : null,
      );
    });
  });
}

function buildSwatchLink(product, { isSwatchProduct, isCurrentProduct }) {
  const label = ((product.swatch && product.swatch.label) || product.title || "").split(" - ")[0].trim();

  const link = document.createElement("a");

  link.href = product.url || "#";
  link.className = "product-swatch";
  link.dataset.productSwatchLink = "";
  link.dataset.productId = product.id;
  link.dataset.colorLabel = label;
  link.dataset.colorFamilyPosition = product.colorFamilyPosition;

  if (isSwatchProduct) {
    link.classList.add("product-swatch--card");
  }

  if (isCurrentProduct) {
    link.classList.add("product-swatch--selected");
    link.setAttribute("aria-current", "page");
  }

  if (product.available === false) {
    link.classList.add("sold-out");
  }

  link.title = label;
  link.setAttribute("aria-label", product.available === false ? `${label} - Sold out` : label);

  if (isSwatchProduct) {
    const imageWrapper = document.createElement("span");

    imageWrapper.className = "product-swatch__image-wrapper";

    if (product.imageUrl) {
      const image = document.createElement("img");

      image.src = product.imageUrl;
      image.alt = label;
      image.loading = "lazy";
      image.width = 200;
      image.height = 200;
      image.className = "product-swatch__image";

      imageWrapper.appendChild(image);
    }

    const labelEl = document.createElement("span");

    labelEl.className = "product-swatch__label body";
    labelEl.textContent = label;

    link.append(imageWrapper, labelEl);

    return link;
  }

  const colorEl = document.createElement("span");

  colorEl.className = "product-swatch__color";

  if (product.swatch && product.swatch.imageUrl) {
    colorEl.style.backgroundImage = `url(${product.swatch.imageUrl})`;
    colorEl.style.backgroundSize = "cover";
  } else if (product.swatch && product.swatch.color) {
    colorEl.style.backgroundColor = product.swatch.color;
  } else {
    colorEl.classList.add("product-swatch__color--placeholder");
  }

  const tooltip = document.createElement("span");

  tooltip.className = "product-swatch__tooltip";
  tooltip.setAttribute("aria-hidden", "true");
  tooltip.textContent = label;

  link.append(colorEl, tooltip);

  return link;
}

function ensureSelectedColorLabel(groupEl, fieldProducts) {
  const currentProductId = groupEl.dataset.currentProductId;

  if (!currentProductId) {
    return;
  }

  const titleEl = groupEl.querySelector(".product-color-groups__title");

  if (!titleEl) {
    return;
  }

  let labelEl = titleEl.querySelector(".product-color-groups__selected-color");

  if (labelEl && labelEl.textContent.trim()) {
    return;
  }

  const currentProduct = fieldProducts.find((product) => product.id === currentProductId);
  const label = currentProduct && currentProduct.swatch && currentProduct.swatch.label;

  if (!label) {
    return;
  }

  if (!labelEl) {
    labelEl = document.createElement("span");
    labelEl.className = "product-color-groups__selected-color body";

    const titleLabelEl = titleEl.querySelector(".product-color-groups__title-label");

    if (titleLabelEl) {
      titleLabelEl.insertAdjacentElement("afterend", labelEl);
    } else {
      titleEl.appendChild(labelEl);
    }
  }

  labelEl.textContent = label;
}

function normalizeColor(value = "") {
  return value.trim().toLowerCase();
}

function getSwatchColor(link) {
  return normalizeColor(link.dataset.colorLabel || link.getAttribute("title") || "");
}

function dedupeSwatchesByColor(swatchesContainer, currentProductId) {
  const swatches = Array.from(swatchesContainer.querySelectorAll("[data-product-swatch-link]"));

  const byColor = new Map();

  swatches.forEach((link) => {
    const color = getSwatchColor(link);

    if (!color) return;

    if (!byColor.has(color)) {
      byColor.set(color, []);
    }

    byColor.get(color).push(link);
  });

  byColor.forEach((links) => {
    if (links.length <= 1) {
      return;
    }

    /*
     * Prefer the CURRENT product for the current color.
     *
     * Example:
     * Baby Pink No Slit
     * Baby Pink Slit ← current
     *
     * Keep Slit.
     */
    const currentLink = links.find((link) => String(link.dataset.productId) === String(currentProductId));

    const keepLink = currentLink || links[0];

    links.forEach((link) => {
      if (link !== keepLink) {
        link.remove();
      }
    });
  });
}

function getSwatchFamilyPosition(link) {
  const position = Number.parseInt(link.dataset.colorFamilyPosition, 10);

  return Number.isFinite(position) ? position : NO_COLOR_FAMILY_POSITION;
}

/*
 * Group swatches by Color Family position (Greens, Blues, ...). Liquid already
 * renders in this order; this re-applies it after swatches are appended.
 * Array.sort is stable, so the merchant's order within each family is kept.
 */
function sortSwatchesByColorFamily(swatchesContainer) {
  const swatches = Array.from(swatchesContainer.querySelectorAll("[data-product-swatch-link]"));
  const sorted = [...swatches].sort((a, b) => getSwatchFamilyPosition(a) - getSwatchFamilyPosition(b));

  if (sorted.every((link, index) => link === swatches[index])) {
    return;
  }

  swatchesContainer.append(...sorted);
}

/*
 * Mirrors the Liquid filter in snippet.product-form-selectors-dress.liquid (and
 * snippet.product-fabric-handle.liquid): on fabric-tabbed PDPs, a product is left
 * out of a group when its own fabric (EXACT handle or label match) is a DIFFERENT
 * fabric AND it is also listed in that fabric's group, so e.g. a Satin Jacquard
 * swatch listed in both Satin and Satin Jacquard only shows under Satin Jacquard.
 */
function buildFabricContext(groupEls) {
  const fabrics = [];
  const memberships = new Set();

  groupEls.forEach((groupEl) => {
    const handle = groupEl.dataset.groupFabricHandle;

    if (!handle) {
      return;
    }

    if (!fabrics.some((fabric) => fabric.handle === handle)) {
      fabrics.push({ handle, label: groupEl.dataset.groupFabricLabel || "" });
    }

    const groupData = groupDataCache.get(groupEl.dataset.groupOptionId);

    if (!groupData) {
      return;
    }

    [...groupData.solids, ...groupData.florals].forEach((product) => {
      memberships.add(`${product.id}:${handle}`);
    });
  });

  return { fabrics, memberships };
}

function resolveProductFabricHandle(product, fabrics) {
  if (!product.fabric) {
    return null;
  }

  const byHandle = fabrics.find((fabric) => fabric.handle === product.fabric.handle);

  if (byHandle) {
    return byHandle.handle;
  }

  const byLabel = product.fabric.label && fabrics.find((fabric) => fabric.label === product.fabric.label);

  return byLabel ? byLabel.handle : null;
}

function filterProductsByGroupFabric(groupEl, products, fabricContext) {
  const groupFabricHandle = groupEl.dataset.groupFabricHandle;

  if (!groupFabricHandle || !fabricContext) {
    return products;
  }

  return products.filter((product) => {
    const productFabricHandle = resolveProductFabricHandle(product, fabricContext.fabrics);

    return (
      !productFabricHandle ||
      productFabricHandle === groupFabricHandle ||
      !fabricContext.memberships.has(`${product.id}:${productFabricHandle}`)
    );
  });
}

function expandGroup(groupEl, fabricContext) {
  const groupData = groupDataCache.get(groupEl.dataset.groupOptionId);

  if (!groupData) {
    return;
  }

  const fieldProducts = filterProductsByGroupFabric(groupEl, groupData[groupEl.dataset.fieldKey] || [], fabricContext);

  if (!fieldProducts || !fieldProducts.length) {
    return;
  }

  ensureSelectedColorLabel(groupEl, fieldProducts);

  const swatchesContainer = groupEl.querySelector(".product-swatches");

  if (!swatchesContainer) {
    return;
  }

  const currentProductId = groupEl.dataset.currentProductId;
  const isSwatchProduct = groupEl.dataset.isSwatchProduct === "true";

  /*
   * FIRST:
   * Collapse duplicates that may already exist from
   * Liquid + hydration.
   */
  dedupeSwatchesByColor(swatchesContainer, currentProductId);

  /*
   * Liquid only renders the first 50 products of a group, so when the
   * current product sits past that cutoff, a sibling sharing its color
   * (Slit/No Slit, fit variants) may already occupy that color's slot.
   * Swap the current product into that slot so it renders as selected.
   */
  const currentProduct = fieldProducts.find((product) => String(product.id) === String(currentProductId));

  const currentColor = currentProduct
    ? normalizeColor(((currentProduct.swatch && currentProduct.swatch.label) || "").split(" - ")[0])
    : "";

  if (
    currentProduct &&
    !swatchesContainer.querySelector(`[data-product-swatch-link][data-product-id="${currentProductId}"]`)
  ) {
    const siblingLink =
      currentColor &&
      Array.from(swatchesContainer.querySelectorAll("[data-product-swatch-link]")).find(
        (link) => getSwatchColor(link) === currentColor,
      );

    if (siblingLink) {
      siblingLink.replaceWith(buildSwatchLink(currentProduct, { isSwatchProduct, isCurrentProduct: true }));
    }
  }

  /*
   * Build the set of COLORS already displayed.
   * Product IDs intentionally do not matter here.
   */
  const renderedColors = new Set(
    Array.from(swatchesContainer.querySelectorAll("[data-product-swatch-link]")).map(getSwatchColor).filter(Boolean),
  );

  const missingProducts = [];

  fieldProducts.forEach((fieldProduct) => {
    const label = (fieldProduct.swatch && fieldProduct.swatch.label) || "";

    const color = normalizeColor(label.split(" - ")[0]);

    /*
     * The current product wins its color's slot over an earlier
     * sibling with the same color, keeping that slot's position.
     */
    const product = color && color === currentColor ? currentProduct : fieldProduct;

    if (!color) {
      return;
    }

    if (renderedColors.has(color)) {
      return;
    }

    /*
     * Claim color immediately so another Slit/No Slit
     * or Slim/Modern product cannot also be appended.
     */
    renderedColors.add(color);

    missingProducts.push(product);
  });

  if (missingProducts.length) {
    const fragment = document.createDocumentFragment();

    missingProducts.forEach((product) => {
      fragment.appendChild(
        buildSwatchLink(product, {
          isSwatchProduct,
          isCurrentProduct: String(currentProductId) === String(product.id),
        }),
      );
    });

    swatchesContainer.appendChild(fragment);
  }

  /*
   * FINAL safety pass.

   * Makes expandGroup() safe to run repeatedly after
   * product:ajax-rendered.
   */
  dedupeSwatchesByColor(swatchesContainer, currentProductId);

  sortSwatchesByColorFamily(swatchesContainer);
}

function findCurrentProductLifestyleColorType(currentProductId) {
  for (const groupData of groupDataCache.values()) {
    if (!groupData) continue;

    if (groupData.solids.some((product) => product.id === currentProductId)) {
      return "solid";
    }

    if (groupData.florals.some((product) => product.id === currentProductId)) {
      return "floral";
    }
  }

  return null;
}

function resolveGalleryLifestyleImages(root) {
  const mediaGallery = root.querySelector("media-gallery");

  if (!mediaGallery || mediaGallery.dataset.lifestyleColorType) {
    return;
  }

  const currentProductId = mediaGallery.dataset.currentProductId;

  if (!currentProductId) {
    return;
  }

  const colorType = findCurrentProductLifestyleColorType(currentProductId);

  if (!colorType || typeof mediaGallery.appendLifestyleSlides !== "function") {
    return;
  }

  mediaGallery.appendLifestyleSlides(colorType);
}

export async function expandProductSwatchGroups(root = document) {
  const groupEls = Array.from(root.querySelectorAll("[data-group-option-id][data-field-key]"));

  if (groupEls.length) {
    const groupOptionIds = [...new Set(groupEls.map((el) => el.dataset.groupOptionId))];
    const uncachedIds = groupOptionIds.filter((id) => !groupDataCache.has(id));

    if (uncachedIds.length) {
      await fetchGroupData(uncachedIds);
    }

    const fabricContext = buildFabricContext(groupEls);

    groupEls.forEach((groupEl) => expandGroup(groupEl, fabricContext));
  }

  resolveGalleryLifestyleImages(root);
}

expandProductSwatchGroups(document);

document.addEventListener("product:ajax-rendered", (event) => {
  expandProductSwatchGroups(event.detail.section || document);
});
